import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Palette, Brush, Droplet, Shirt, Scissors, ArrowRight, Wand2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { IMG } from "@/lib/images";
import AdSlot from "@/components/AdSlot";

const fade = { hidden: { opacity: 0, y: 26 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }) };

const BRANDS = ["MAC", "Dior", "Chanel", "Rare Beauty", "Fenty Beauty", "Charlotte Tilbury", "NARS", "Kiko Milano", "YSL Beauty", "Huda Beauty"];

function Feature({ to, icon: Icon, title, desc, img, className, testid }) {
  return (
    <Link to={to} data-testid={testid} className={`group relative overflow-hidden rounded-2xl border border-rose-100 bg-white hover-lift ${className}`}>
      {img && (
        <div className="absolute inset-0">
          <img src={img} alt="" className="w-full h-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/20 to-transparent" />
        </div>
      )}
      <div className={`relative p-6 md:p-7 flex flex-col h-full ${img ? "text-white justify-end" : ""}`}>
        <span className={`w-11 h-11 rounded-full flex items-center justify-center mb-3 ${img ? "bg-white/20 backdrop-blur" : "bg-rose-100 text-rose-600"}`}>
          <Icon size={20} />
        </span>
        <h3 className="font-serif text-xl md:text-2xl">{title}</h3>
        <p className={`mt-1 text-sm ${img ? "text-white/80" : "text-charcoal/55"}`}>{desc}</p>
        <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all">
          Scopri <ArrowRight size={15} />
        </span>
      </div>
    </Link>
  );
}

export default function Landing() {
  const { t } = useI18n();
  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10 items-center pt-14 md:pt-20 pb-16">
          <div>
            <motion.span variants={fade} initial="hidden" animate="show" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-rose-500 bg-rose-50 rounded-full px-4 py-2">
              <Sparkles size={14} /> {t("hero.tag")}
            </motion.span>
            <motion.h1 variants={fade} custom={1} initial="hidden" animate="show" className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-charcoal">
              {t("hero.title").split(" ").slice(0, 3).join(" ")}{" "}
              <span className="text-gradient-gold">{t("hero.title").split(" ").slice(3).join(" ")}</span>
            </motion.h1>
            <motion.p variants={fade} custom={2} initial="hidden" animate="show" className="mt-5 text-lg text-charcoal/60 max-w-lg leading-relaxed">
              {t("hero.sub")}
            </motion.p>
            <motion.div variants={fade} custom={3} initial="hidden" animate="show" className="mt-8 flex flex-wrap gap-3">
              <Link to="/quiz" data-testid="hero-cta-quiz" className="inline-flex items-center gap-2 rounded-full bg-rose-400 hover:bg-rose-500 text-white font-semibold px-7 py-3.5 transition-all hover:-translate-y-0.5 shadow-lg shadow-rose-200">
                <Wand2 size={18} /> {t("hero.cta")}
              </Link>
              <Link to="/catalog/makeup" data-testid="hero-cta-catalog" className="inline-flex items-center gap-2 rounded-full border border-charcoal/15 hover:border-rose-300 px-7 py-3.5 font-semibold transition-colors">
                {t("hero.cta2")}
              </Link>
            </motion.div>
            <motion.div variants={fade} custom={4} initial="hidden" animate="show" className="mt-10 flex items-center gap-6 text-sm text-charcoal/50">
              <div><span className="block font-serif text-2xl text-charcoal">6.400+</span> prodotti</div>
              <div className="w-px h-8 bg-rose-100" />
              <div><span className="block font-serif text-2xl text-charcoal">12</span> stagioni armocromia</div>
              <div className="w-px h-8 bg-rose-100" />
              <div><span className="block font-serif text-2xl text-charcoal">90+</span> brand</div>
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="relative">
            <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] shadow-2xl">
              <img src={IMG.hero} alt="Velyrea beauty consultant" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-5 glass rounded-2xl p-4 shadow-xl animate-float hidden sm:block">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full rose-gold-bg" />
                <div>
                  <p className="text-xs text-charcoal/50">La tua stagione</p>
                  <p className="font-serif text-lg">Inverno Profondo</p>
                </div>
                <span className="ml-2 text-xs font-bold text-white bg-gold-dark rounded-full px-2 py-1">92%</span>
              </div>
            </div>
            <div className="absolute -top-4 -right-3 glass rounded-2xl px-4 py-3 shadow-xl hidden sm:flex items-center gap-2">
              <Palette size={18} className="text-rose-500" />
              <span className="text-sm font-medium">Palette personale</span>
            </div>
          </motion.div>
        </div>

        {/* brand strip */}
        <div className="border-y border-rose-100 bg-surface/60 py-5 overflow-hidden">
          <div className="flex gap-12 whitespace-nowrap animate-[shimmer_none] justify-center flex-wrap px-5">
            {BRANDS.map((b) => <span key={b} className="font-serif text-lg text-charcoal/35">{b}</span>)}
          </div>
        </div>
      </section>

      {/* BENTO FEATURES */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20">
        <div className="max-w-xl mb-10">
          <p className="text-xs uppercase tracking-[0.3em] text-rose-500 font-semibold">Tutto in un unico posto</p>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight">Il tuo consulente di bellezza completo</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4 auto-rows-[220px]">
          <Feature testid="feat-armocromia" to="/armocromia" icon={Palette} title={t("nav.armocromia")} desc="Scopri la tua stagione e la palette perfetta" img={IMG.swatches} className="md:row-span-2 md:!h-full" />
          <Feature testid="feat-makeup" to="/catalog/makeup" icon={Brush} title={t("nav.makeup")} desc="Rossetti, fondotinta, palette su misura" img={IMG.makeup} className="md:col-span-2" />
          <Feature testid="feat-skincare" to="/catalog/skincare" icon={Droplet} title={t("nav.skincare")} desc="Routine complete per il tuo tipo di pelle" img={IMG.skincare} />
          <Feature testid="feat-fashion" to="/catalog/fashion" icon={Shirt} title={t("nav.fashion")} desc="Capi nei colori giusti per te" img={IMG.fashion} />
          <Feature testid="feat-outfits" to="/outfits" icon={Sparkles} title={t("nav.outfits")} desc="Look completi generati automaticamente" img={IMG.outfit} />
          <Feature testid="feat-hair" to="/hair" icon={Scissors} title={t("nav.hair")} desc="Tagli e colori ideali per il tuo viso" className="md:col-span-2" img={IMG.hair} />
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 md:px-8"><AdSlot format="leaderboard" /></div>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20">
        <div className="grid lg:grid-cols-3 gap-8">
          {[
            { n: "01", t: "Configura il profilo", d: "Rispondi al quiz: pelle, occhi, capelli, corporatura e stile." },
            { n: "02", t: "Ricevi la tua analisi", d: "Armocromia professionale, avatar dinamico e palette personale." },
            { n: "03", t: "Acquista i migliori match", d: "Prodotti ordinati per compatibilità, con link diretti allo shop." },
          ].map((s, i) => (
            <motion.div key={s.n} variants={fade} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="relative p-8 rounded-2xl bg-surface border border-rose-100">
              <span className="font-serif text-5xl text-rose-200">{s.n}</span>
              <h3 className="mt-3 font-serif text-2xl">{s.t}</h3>
              <p className="mt-2 text-charcoal/55">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24">
        <div className="rose-gold-bg rounded-[2rem] px-8 md:px-16 py-16 text-center text-white relative overflow-hidden">
          <div className="relative">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl">Pronta a scoprire il tuo stile?</h2>
            <p className="mt-4 text-white/90 max-w-xl mx-auto">Crea il tuo profilo gratuito e ricevi consigli personalizzati in pochi secondi.</p>
            <Link to="/quiz" data-testid="cta-bottom-quiz" className="mt-8 inline-flex items-center gap-2 rounded-full bg-charcoal text-white font-semibold px-8 py-4 hover:-translate-y-0.5 transition-transform">
              {t("common.start")} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
